import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CommitImportOutput, ImportBatch, ImportPreviewRow } from 'fuel-benchmark-contracts';
import { ArrowRight, CheckCircle2, Download, Upload } from 'lucide-react';
import { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { orpc } from '../../apis/orpc-query';
import { Button } from '../../components/ui/Button';
import { DataTable, type DataTableColumns } from '../../components/ui/DataTable';
import { StatCard } from '../../components/ui/DefinitionList';
import { InfoCell } from '../../components/ui/InfoCell';
import { StatusText } from '../../components/ui/StatusText';
import { formatDayTime, formatMeasurement } from '../../lib/format';
import { submitErrorKey, serverMessage } from '../../lib/form-helpers';
import { useI18nTranslation } from '../../lib/use-i18n-translation';
import { runStatusTone } from '../runs/run-display';
import { BatchErrorDialog } from './BatchErrorDialog';
import { ACCEPTED_UPLOAD_EXTENSIONS, batchStatusTone, readFileAsBase64, saveBase64File, verdictTone, type UploadPayload } from './import-display';

const HISTORY_PAGE_SIZE = 25;

/**
 * Spreadsheet import (plan §7). Three sections in the order the work happens: take the template, upload a
 * filled-in copy and read the dry run, then look back at what past imports did.
 *
 * The preview is the whole point of the screen — nothing is written until the commit button is pressed, and
 * the commit sends the very same file again, so what the table showed is what gets imported.
 */
export const ImportPage = () => {
  const { t } = useI18nTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [upload, setUpload] = useState<UploadPayload | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [committed, setCommitted] = useState<CommitImportOutput | null>(null);
  const [openBatchId, setOpenBatchId] = useState<string | null>(null);

  const template = useMutation(orpc.import.downloadTemplate.mutationOptions());
  const preview = useMutation(orpc.import.preview.mutationOptions());

  const commit = useMutation(
    orpc.import.commit.mutationOptions({
      onSuccess: async (result: CommitImportOutput) => {
        setCommitted(result);
        preview.reset();
        setUpload(null);

        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }

        await Promise.all([
          queryClient.invalidateQueries({ queryKey: orpc.import.listBatches.key() }),
          queryClient.invalidateQueries({ queryKey: orpc.runs.list.key() }),
          queryClient.invalidateQueries({ queryKey: orpc.benchmarks.dashboardStats.key() }),
          queryClient.invalidateQueries({ queryKey: orpc.benchmarks.coverageMatrix.key() }),
        ]);
      },
    }),
  );

  const batches = useQuery(
    orpc.import.listBatches.queryOptions({ input: { page: 1, pageSize: HISTORY_PAGE_SIZE, sortBy: 'createdAt', sortDirection: 'desc' } }),
  );

  const summary = preview.data?.summary ?? null;
  const importable = summary === null ? 0 : summary.accept + summary.warn;

  const onPickFile = async (file: File | undefined): Promise<void> => {
    setFileError(null);
    setCommitted(null);
    preview.reset();
    commit.reset();
    setUpload(null);

    if (!file) {
      return;
    }

    try {
      const payload = await readFileAsBase64(file);

      setUpload(payload);
      preview.mutate(payload);
    } catch {
      setFileError(t('import.upload.readFailed'));
    }
  };

  const previewColumns: DataTableColumns<ImportPreviewRow> = useMemo(
    () => [
      {
        accessorKey: 'rowNumber',
        header: t('import.columns.row'),
        cell: ({ row }) => <span className="font-mono text-xs text-muted-foreground">{row.original.rowNumber}</span>,
      },
      {
        id: 'vehicle',
        accessorFn: (row) => row.resolved.vehicleCode ?? '',
        header: t('import.columns.vehicle'),
        cell: ({ row }) => <span className="whitespace-nowrap">{row.original.resolved.vehicleCode ?? '—'}</span>,
      },
      {
        id: 'scenario',
        accessorFn: (row) => row.resolved.scenarioCode ?? '',
        header: t('import.columns.scenario'),
        cell: ({ row }) => (
          <InfoCell
            title={row.original.resolved.scenarioCode ?? '—'}
            subline={row.original.resolved.routeCode}
          />
        ),
      },
      {
        id: 'result',
        accessorFn: (row) => row.computedResult?.value ?? null,
        header: t('import.columns.result'),
        cell: ({ row }) => (
          <span className="whitespace-nowrap font-medium">
            {row.original.computedResult === null ? '—' : formatMeasurement(row.original.computedResult.value, row.original.computedResult.unit, 1)}
          </span>
        ),
      },
      {
        id: 'wouldBeStatus',
        accessorFn: (row) => row.wouldBeStatus ?? '',
        header: t('import.columns.wouldBeStatus'),
        cell: ({ row }) =>
          row.original.wouldBeStatus === null ? (
            <span className="text-muted-foreground">—</span>
          ) : (
            <StatusText
              label={t(`enums.runStatus.${row.original.wouldBeStatus}`)}
              tone={runStatusTone(row.original.wouldBeStatus)}
            />
          ),
      },
      {
        accessorKey: 'verdict',
        header: t('import.columns.verdict'),
        cell: ({ row }) => (
          <StatusText
            label={t(`import.verdict.${row.original.verdict}`)}
            tone={verdictTone(row.original.verdict)}
          />
        ),
      },
      {
        accessorKey: 'reason',
        header: t('import.columns.reason'),
        cell: ({ row }) => <span className="text-sm text-muted-foreground">{row.original.reason ?? '—'}</span>,
      },
    ],
    [t],
  );

  const historyColumns: DataTableColumns<ImportBatch> = useMemo(
    () => [
      {
        accessorKey: 'createdAt',
        header: t('import.columns.date'),
        cell: ({ row }) => <span className="whitespace-nowrap">{formatDayTime(row.original.createdAt)}</span>,
      },
      {
        accessorKey: 'filename',
        header: t('import.columns.file'),
        cell: ({ row }) => (
          <InfoCell
            title={row.original.filename}
            subline={row.original.uploadedByName}
          />
        ),
      },
      { accessorKey: 'rowCount', header: t('import.columns.rows') },
      {
        id: 'accepted',
        accessorFn: (batch) => batch.acceptedCount,
        header: t('import.columns.accepted'),
        cell: ({ row }) => <span className="whitespace-nowrap font-medium">{row.original.acceptedCount}</span>,
      },
      {
        id: 'rejected',
        accessorFn: (batch) => batch.rejectedCount,
        header: t('import.columns.rejected'),
        cell: ({ row }) =>
          row.original.rejectedCount === 0 ? (
            <span className="text-muted-foreground">0</span>
          ) : (
            <StatusText
              label={String(row.original.rejectedCount)}
              tone="danger"
            />
          ),
      },
      {
        accessorKey: 'status',
        header: t('import.columns.status'),
        cell: ({ row }) => (
          <StatusText
            label={t(`import.batchStatus.${row.original.status}`)}
            tone={batchStatusTone(row.original.status)}
          />
        ),
      },
    ],
    [t],
  );

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-foreground">{t('import.title')}</h1>
        <p className="mt-1 text-sm text-muted-foreground">{t('import.subtitle')}</p>
      </div>

      <section className="space-y-3 rounded-lg border border-border bg-card p-5">
        <div>
          <h2 className="text-base font-semibold text-card-foreground">{t('import.template.title')}</h2>
          <p className="mt-1 text-sm text-muted-foreground">{t('import.template.body')}</p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            isLoading={template.isPending && template.variables?.format === 'xlsx'}
            onClick={() => template.mutate({ format: 'xlsx' }, { onSuccess: saveBase64File })}
          >
            <Download className="size-4" />
            {t('import.template.xlsx')}
          </Button>
          <Button
            variant="outline"
            isLoading={template.isPending && template.variables?.format === 'csv'}
            onClick={() => template.mutate({ format: 'csv' }, { onSuccess: saveBase64File })}
          >
            <Download className="size-4" />
            {t('import.template.csv')}
          </Button>
        </div>

        {template.isError ? (
          <p
            role="alert"
            className="text-sm text-destructive"
          >
            {t('errors.loadFailed')}
          </p>
        ) : null}
      </section>

      <section className="space-y-4 rounded-lg border border-border bg-card p-5">
        <div>
          <h2 className="text-base font-semibold text-card-foreground">{t('import.upload.title')}</h2>
          <p className="mt-1 text-sm text-muted-foreground">{t('import.upload.body')}</p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <input
            ref={fileInputRef}
            id="import-file"
            type="file"
            accept={ACCEPTED_UPLOAD_EXTENSIONS}
            aria-label={t('import.upload.choose')}
            onChange={(event) => void onPickFile(event.target.files?.[0])}
            className="h-9 max-w-md rounded-md border border-border bg-background px-3 text-sm text-foreground file:me-3 file:h-7 file:rounded file:border-0 file:bg-muted file:px-3 file:text-xs file:font-medium file:text-foreground"
          />
          {upload ? <span className="font-mono text-xs text-muted-foreground">{upload.filename}</span> : null}
        </div>

        {fileError ? (
          <p
            role="alert"
            className="text-sm text-destructive"
          >
            {fileError}
          </p>
        ) : null}

        {preview.isError ? (
          <p
            role="alert"
            className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive"
          >
            {serverMessage(preview.error) ?? t(submitErrorKey(preview.error))}
          </p>
        ) : null}

        {preview.isPending ? <p className="text-sm text-muted-foreground">{t('import.upload.checking')}</p> : null}

        {summary ? (
          <>
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
              <StatCard
                label={t('import.summary.total')}
                value={summary.total}
              />
              <StatCard
                label={t('import.summary.accept')}
                value={summary.accept}
                hint={t('import.summary.acceptHint')}
              />
              <StatCard
                label={t('import.summary.warn')}
                value={summary.warn}
                hint={t('import.summary.warnHint')}
              />
              <StatCard
                label={t('import.summary.reject')}
                value={summary.reject}
                hint={t('import.summary.rejectHint')}
              />
            </div>

            <DataTable
              columns={previewColumns}
              data={preview.data?.rows ?? []}
              emptyMessage={t('import.upload.noRows')}
              pageSize={HISTORY_PAGE_SIZE}
            />

            {commit.isError ? (
              <p
                role="alert"
                className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive"
              >
                {serverMessage(commit.error) ?? t(submitErrorKey(commit.error))}
              </p>
            ) : null}

            <div className="flex flex-wrap items-center gap-3">
              <Button
                disabled={importable === 0 || upload === null}
                isLoading={commit.isPending}
                onClick={() => (upload === null ? undefined : commit.mutate(upload))}
              >
                <Upload className="size-4" />
                {t('import.upload.commit', { n: importable })}
              </Button>
              <p className="text-xs text-muted-foreground">
                {importable === 0 ? t('import.upload.nothingToCommit') : t('import.upload.commitHint', { rejected: summary.reject })}
              </p>
            </div>
          </>
        ) : null}

        {committed ? (
          <div className="space-y-3 rounded-md border border-border bg-muted/40 p-4">
            <div className="flex items-start gap-3">
              <CheckCircle2
                className="mt-0.5 size-5 shrink-0"
                style={{ color: '#15803d' }}
                aria-hidden
              />
              <div className="min-w-0">
                <p className="text-sm font-medium text-foreground">{t('import.success.title', { n: committed.runIds.length })}</p>
                <p className="mt-1 text-sm text-muted-foreground">
                  {t('import.history.counts', {
                    rows: committed.batch.rowCount,
                    accepted: committed.batch.acceptedCount,
                    rejected: committed.batch.rejectedCount,
                  })}
                </p>
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                disabled={committed.runIds.length === 0}
                onClick={() => navigate(`/runs?importBatchId=${committed.batch.id}`)}
              >
                {t('import.success.viewRuns')}
                <ArrowRight className="size-4" />
              </Button>
              {committed.batch.rejectedCount > 0 ? (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setOpenBatchId(committed.batch.id)}
                >
                  {t('import.success.viewErrors')}
                </Button>
              ) : null}
            </div>
          </div>
        ) : null}
      </section>

      <section className="space-y-3">
        <div>
          <h2 className="text-base font-semibold text-foreground">{t('import.history.title')}</h2>
          <p className="mt-1 text-sm text-muted-foreground">{t('import.history.body')}</p>
        </div>

        {batches.isError ? (
          <p
            role="alert"
            className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive"
          >
            {t('errors.loadFailed')}
          </p>
        ) : null}

        <DataTable
          columns={historyColumns}
          data={batches.data?.items ?? []}
          isLoading={batches.isPending}
          emptyMessage={t('import.history.empty')}
          onRowClick={(batch) => setOpenBatchId(batch.id)}
        />
      </section>

      <BatchErrorDialog
        batchId={openBatchId}
        onClose={() => setOpenBatchId(null)}
      />
    </div>
  );
};
